import type { CategoryType } from '@/shared/constants/requiredFieldsMappings';
import type { RequiredFieldConfig } from '../types/requiredFields.types';
import { createRequiredFieldsApi } from '../api/requiredFieldsApi';

/**
 * Service layer for Required Fields functionality
 * Provides business logic on top of the API layer
 */
export const createRequiredFieldsService = (
  requiredFieldsApi: ReturnType<typeof createRequiredFieldsApi>
) => ({
  /**
   * Get required fields configuration for a specific category
   */
  getRequiredFieldsForCategory: async (category: CategoryType): Promise<RequiredFieldConfig> => {
    return requiredFieldsApi.getRequiredFields(category);
  },

  /**
   * Update required fields for a category
   */
  updateRequiredFieldsForCategory: async (
    category: CategoryType,
    selectedFieldKeys: string[]
  ): Promise<{ success: boolean; message: string }> => {
    return requiredFieldsApi.updateRequiredFields(category, selectedFieldKeys);
  },

  /**
   * Get all required fields configurations
   */
  getAllRequiredFieldsConfigs: async (): Promise<Record<CategoryType, RequiredFieldConfig>> => {
    return requiredFieldsApi.getAllRequiredFields();
  },

  /**
   * Validate if required fields are properly configured
   */
  validateRequiredFields: (config: RequiredFieldConfig): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Ensure mandatory fields are always included
    const mandatoryKeys = config.mandatoryFields.map((f) => f.fieldKey);
    const hasAllMandatory = mandatoryKeys.every((key) => 
      config.mandatoryFields.find((f) => f.fieldKey === key && f.isRequired)
    );

    if (!hasAllMandatory) {
      errors.push('All mandatory fields must be marked as required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
});